<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { clearLocalSession, useLogout } from '@/composables/auth'
import { clearPersistedReportCommentDrafts } from '@/utilities/commentDrafts'
import {
  getBroadcastChannel,
  broadcastTabId,
  sessionChangedReason,
  SESSION_CHANGE_REASONS,
  type BroadcastMessage,
} from '@/utilities/broadcaster'

const router = useRouter()
const { logout } = useLogout()

// Cross-tab events via BroadcastChannel
getBroadcastChannel()?.addEventListener('message', (event: MessageEvent<BroadcastMessage>) => {
  if (event.data.tab !== broadcastTabId) {
    sessionChangedReason.value = event.data.message
  }
})

const sessionChangedModalVisible = computed(() => sessionChangedReason.value !== undefined)

const sessionChangedModalTitle = computed(() => {
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.USER_CHANGED)
    return 'Your user has changed'
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.LOGGED_OUT)
    return 'Signed out of Ruminati in another tab'
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.ORG_CHANGED)
    return 'Organisation changed in another tab'
  return 'Your session ran out'
})

const sessionChangedModalBody = computed(() => {
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.USER_CHANGED) {
    return 'It looks like you signed in with a different user in another tab or window.'
  }
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.LOGGED_OUT) {
    return 'You were signed out of Ruminati in another tab or window. You can only use one Ruminati user across multiple tabs at a time.'
  }
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.ORG_CHANGED) {
    return 'The active organisation was switched in another tab or window.'
  }
  return 'Your session is no longer valid in this tab.'
})

const sessionChangedModalSubBody = computed(() => {
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.USER_CHANGED) {
    return 'You can only be signed into 1 Ruminati account at a time.'
  }
  return ''
})

const sessionChangedModalActionHint = computed(() => {
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.USER_CHANGED) {
    return 'Either reload to continue with the latest user, or head back to login.'
  }
  if (sessionChangedReason.value === SESSION_CHANGE_REASONS.ORG_CHANGED) {
    return 'Reload to continue in the new organisation, or head back to login.'
  }
  return 'Reload to try again, or head back to login.'
})

function reloadForNewSession() {
  if (
    sessionChangedReason.value === SESSION_CHANGE_REASONS.USER_CHANGED ||
    sessionChangedReason.value === SESSION_CHANGE_REASONS.LOGGED_OUT ||
    sessionChangedReason.value === SESSION_CHANGE_REASONS.SESSION_INVALID
  ) {
    clearPersistedReportCommentDrafts()
  }

  sessionChangedReason.value = undefined
  window.location.reload()
}

async function goToLogin() {
  sessionChangedReason.value = undefined

  try {
    const logoutResult = await logout()
    if (!logoutResult.success) {
      clearLocalSession()
    }
  } catch {
    clearLocalSession()
  }

  sessionChangedReason.value = undefined
  router.push({ name: 'login' })
}
</script>

<template>
  <a-modal
    :open="sessionChangedModalVisible"
    :closable="false"
    :keyboard="false"
    :mask-closable="false"
    centered
    :footer="null"
    :width="900"
    wrap-class-name="sessionModal"
    :mask-style="{
      'background-color': 'rgba(8,8,8,0.6)',
    }"
  >
    <a-flex justify="center" class="gap-x-12 pt-12">
      <h2 class="mb-2">
        {{ sessionChangedModalTitle }}
      </h2>
    </a-flex>

    <a-flex justify="center">
      <a-flex vertical>
        <p class="w-140 text-center">
          {{ sessionChangedModalBody }}
        </p>
        <p v-if="sessionChangedModalSubBody" class="text-rumi-grey-650 w-120 text-center text-sm">
          {{ sessionChangedModalSubBody }}
        </p>
      </a-flex>
    </a-flex>

    <a-flex justify="center" class="m-t-6 p-b-12">
      <a-flex vertical>
        <p>{{ sessionChangedModalActionHint }}</p>
        <a-flex justify="center" class="m-t-4 gap-x-12">
          <a-button @click="goToLogin">Go to login</a-button>
          <a-button type="primary" @click="reloadForNewSession">Reload now</a-button>
        </a-flex>
      </a-flex>
    </a-flex>
    <img src="/images/question.svg" style="position: absolute; bottom: 0px" class="w-[200px]" />
  </a-modal>
</template>

<style lang="scss">
.sessionModal {
  .ant-modal-content {
    padding: 0px;
  }
}
</style>
